import { create } from 'zustand'
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { useAuthStore } from '@/stores/auth'
import { useCityStore } from '@/stores/city'
import { isTournamentArchived } from '@/utils/tournamentRegistration'

export interface AdminInboxCounts {
  pickup: number
  tournament: number
  training: number
  skills: number
  total: number
}

export interface AdminInboxState {
  dismissedThisSession: boolean
  modalOpen: boolean
  loading: boolean
  pickupPending: any[]
  tournamentPending: any[]
  trainingPending: any[]
  skillsPending: any[]
  // derived
  counts: AdminInboxCounts
  hasItems: boolean
  // actions
  dismiss: () => void
  close: () => void
  maybeOpen: () => void
  refresh: () => Promise<void>
}

async function loadPendingApplications (collectionName: string, ids: string[], labelField: string) {
  const rows: any[] = []
  const unique = [...new Set(ids.filter(Boolean))].slice(0, 40)
  await Promise.all(
    unique.map(async (id) => {
      try {
        const snap = await getDocs(
          query(
            collection(db, collectionName, id, 'applications'),
            where('status', '==', 'pending')
          )
        )
        for (const d of snap.docs) {
          const data = d.data() || {}
          rows.push({
            id: d.id,
            parentId: id,
            displayName: data.displayName || data.email || d.id,
            position: data.position,
            [labelField]: id
          })
        }
      } catch (e) {
        console.warn(`[adminInbox] ${collectionName} ${id}:`, e)
      }
    })
  )
  return rows
}

export const useAdminInboxStore = create<AdminInboxState>()(
  derived<AdminInboxState>((s) => {
    const pickup = s.pickupPending.length
    const tournament = s.tournamentPending.length
    const training = s.trainingPending.length
    const skills = s.skillsPending.length
    const counts = {
      pickup,
      tournament,
      training,
      skills,
      total: pickup + tournament + training + skills
    }
    return { counts, hasItems: counts.total > 0 }
  })((set, get) => ({
    dismissedThisSession: false,
    modalOpen: false,
    loading: false,
    pickupPending: [],
    tournamentPending: [],
    trainingPending: [],
    skillsPending: [],
    counts: { pickup: 0, tournament: 0, training: 0, skills: 0, total: 0 },
    hasItems: false,

    dismiss: () => {
      set({ dismissedThisSession: true, modalOpen: false })
    },

    close: () => {
      set({ modalOpen: false })
    },

    maybeOpen: () => {
      if (get().dismissedThisSession) return
      if (!get().hasItems) return
      set({ modalOpen: true })
    },

    refresh: async () => {
      const authStore = useAuthStore.getState()
      if (!authStore.isInboxReviewer) {
        set({
          pickupPending: [],
          tournamentPending: [],
          trainingPending: [],
          skillsPending: []
        })
        return
      }

      set({ loading: true })
      try {
        const pickup: any[] = []
        if (authStore.isSuperAdmin) {
          try {
            const snap = await getDocs(
              query(collection(db, 'regularStatusRequests'), where('status', '==', 'pending'))
            )
            pickup.push(
              ...snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            )
          } catch (e) {
            console.warn('[adminInbox] pickup (all):', e)
          }
        } else {
          const cityStore = useCityStore.getState()
          const cityIds: string[] = authStore.adminCityIds.length
            ? authStore.adminCityIds
            : (cityStore.activeCities || []).map((c) => c.id).filter((id) => authStore.isCityAdmin(id))
          await Promise.all(
            cityIds.map(async (cityId) => {
              try {
                const snap = await getDocs(
                  query(
                    collection(db, 'regularStatusRequests'),
                    where('cityId', '==', cityId),
                    where('status', '==', 'pending')
                  )
                )
                pickup.push(
                  ...snap.docs.map((d) => ({ id: d.id, ...d.data() }))
                )
              } catch (e) {
                console.warn('[adminInbox] pickup', cityId, e)
              }
            })
          )
        }
        set({ pickupPending: pickup })

        const tournamentIds: string[] = authStore.isSuperAdmin
          ? []
          : authStore.tournamentAdminOf
        if (authStore.isSuperAdmin) {
          try {
            const snap = await getDocs(collection(db, 'tournaments'))
            set({
              tournamentPending: await loadPendingApplications(
                'tournaments',
                snap.docs.filter((d) => !isTournamentArchived(d.data())).map((d) => d.id),
                'tournamentId'
              )
            })
          } catch (e) {
            console.warn('[adminInbox] tournaments:', e)
            set({ tournamentPending: [] })
          }
        } else if (tournamentIds.length) {
          set({
            tournamentPending: await loadPendingApplications(
              'tournaments',
              tournamentIds,
              'tournamentId'
            )
          })
        } else {
          set({ tournamentPending: [] })
        }

        const trainingIds: string[] = authStore.isSuperAdmin
          ? []
          : authStore.trainingAdminOf
        if (authStore.isSuperAdmin) {
          try {
            const snap = await getDocs(collection(db, 'trainingSessions'))
            set({
              trainingPending: await loadPendingApplications(
                'trainingSessions',
                snap.docs.map((d) => d.id),
                'trainingSessionId'
              )
            })
          } catch (e) {
            console.warn('[adminInbox] training:', e)
            set({ trainingPending: [] })
          }
        } else if (trainingIds.length) {
          set({
            trainingPending: await loadPendingApplications(
              'trainingSessions',
              trainingIds,
              'trainingSessionId'
            )
          })
        } else {
          set({ trainingPending: [] })
        }

        try {
          const snap = await getDocs(
            query(collection(db, 'skillRatingRequests'), where('status', '==', 'open'))
          )
          set({ skillsPending: snap.docs.map((d) => ({ id: d.id, ...d.data() })) })
        } catch (e) {
          console.warn('[adminInbox] skills:', e)
          set({ skillsPending: [] })
        }
      } finally {
        set({ loading: false })
      }
    }
  }))
)
